import { Button, Card, Checkbox, Flex, Form, Typography } from 'antd';
import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import LoadingComponent from 'components/loadingComponent/loadingComponent';
import PageTitle from 'components/title/title';
import BackButton from 'components/backButton/backButton';
import ComponentHeader from 'components/componentHeader/componentHeader';
import DisabledElement from 'components/disabledElement/disabledElement';
import StatusComponent from 'components/status/status';
import PriceComponent from '../components/priceComponent';
import Riyal from 'components/riyal/riyal';
import NumberElement from 'components/number/number';

import NotificationService from 'core/services/notification';
import { onGetAuthorizedCapital, onUpdateAuthorizedCapital } from '../services/authorizedCapital';

import { AuthorizedCapital } from '../models/raiseCapitals';

const UpdateAuthorizedCapitalPage: FunctionComponent = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const [updateForm] = Form.useForm();

	const authorizedWatcher = Form.useWatch('authorized_capital', updateForm);
	const [newRatio, setNewRatio] = useState<number>(100);

	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [authorizedCapital, setAuthorizedCapital] = useState<AuthorizedCapital>();

	const [acknowledge, setAcknowledge] = useState<boolean>(false);
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

	useEffect(() => {
		getAuthorizedCapital();
	}, []);

	useEffect(() => {
		setNewRatio(
			authorizedWatcher && authorizedCapital
				? (parseFloat(authorizedCapital.issued_capital.replaceAll(',', '')) / parseFloat(authorizedWatcher.toString().replaceAll(',', ''))) * 100
				: 0,
		);
	}, [authorizedWatcher]);

	const getAuthorizedCapital = async () => {
		try {
			setIsLoading(true);

			const response = await onGetAuthorizedCapital();
			setAuthorizedCapital(response.success);
			updateForm.setFieldsValue({ old_authorized_capital: response.success.authorized_capital.replaceAll(',', '') });

			setIsLoading(false);
		} catch (error) {
			setIsLoading(false);
		}
	};

	const updateAuthorizedCapital = async () => {
		try {
			setIsSubmitting(true);

			await onUpdateAuthorizedCapital(updateForm.getFieldValue('authorized_capital'));

			navigate('/dashboard/actions/capitals');

			NotificationService.success(t('COMMON.SUCCESS'), t('ACTIONS.AUTHORIZED_CAPITAL_UPDATED_SUCCESSFULLY'));
		} catch (error) {}
	};

	return (
		<>
			<PageTitle name={t('TITLE.ACTIONS')} />

			<BackButton />

			{isLoading ? (
				<LoadingComponent />
			) : (
				<>
					<Card>
						<ComponentHeader title={t('ACTIONS.UPDATE_AUTHORIZED_CAPITAL')} subtitle={t('ACTIONS.FILL_TO_BUILD')} />

						<Form
							form={updateForm}
							style={{ marginTop: 30 }}
							onFinish={updateAuthorizedCapital}
							initialValues={{ authorized_capital: authorizedCapital?.authorized_capital.replaceAll(',', '') }}>
							<DisabledElement
								value={<PriceComponent form={updateForm} fieldName="old_authorized_capital" viewOnly transparent />}
								label={t('ACTIONS.CURRENT_AUTHORIZED_CAPITAL')}
								style={{ maxWidth: '100%', width: '1000px', flexWrap: 'wrap' }}
								addonAfter={
									<StatusComponent
										color="blue"
										bordered
										style={{ minWidth: 569 }}
										text={
											<Flex gap={5} align="center">
												<span>{t('ACTIONS.ISSUED_CAPITAL_IS')}</span>

												<span className="fw-medium mx-1 d-flex align-items-center gap-1" style={{ direction: 'ltr' }}>
													<Riyal size={13} />
													<NumberElement
														style={{ fontSize: 15, fontWeight: 500 }}
														value={+authorizedCapital?.issued_capital.replaceAll(',', '')!}
														decimalScale={2}
													/>
												</span>

												<span>{t('ACTIONS.WHICH_IS')}</span>

												<Flex gap={2} className="fw-medium">
													<NumberElement style={{ fontSize: 15, fontWeight: 500 }} value={+authorizedCapital?.ratio!} decimalScale={2} /> %
												</Flex>

												<span>{t('ACTIONS.OF_AUTHORIZED_CAPITAL')}</span>
											</Flex>
										}
									/>
								}
							/>

							<Typography.Text style={{ fontSize: 15, marginBottom: 5, display: 'inline-block' }}>
								{t('ACTIONS.NEW_AUTHORIZED_CAPITAL')}
							</Typography.Text>
							<Flex
								align="center"
								justify="space-between"
								wrap
								gap={20}
								style={{
									background: '#f9fafb',
									border: '1px solid #d9d9d9',
									padding: '8px 11px',
									borderRadius: 'var(--radius)',
									maxWidth: '100%',
									width: '1000px',
								}}>
								<PriceComponent form={updateForm} fieldName="authorized_capital" />

								{newRatio <= 100 ? (
									<StatusComponent
										color="blue"
										bordered
										style={{ minWidth: 569 }}
										text={
											<Flex gap={5} align="center">
												<span>{t('ACTIONS.ISSUED_CAPITAL_IS')}</span>

												<span className="fw-medium mx-1 d-flex align-items-center gap-1" style={{ direction: 'ltr' }}>
													<Riyal size={13} />
													<NumberElement
														style={{ fontSize: 15, fontWeight: 500 }}
														value={+authorizedCapital?.issued_capital.replaceAll(',', '')!}
														decimalScale={2}
													/>
												</span>

												<span>{t('ACTIONS.WHICH_IS')}</span>

												<Flex gap={2} className="fw-medium">
													<NumberElement style={{ fontSize: 15, fontWeight: 500 }} value={newRatio} decimalScale={2} /> %
												</Flex>

												<span>{t('ACTIONS.OF_NEW_AUTHORIZED_CAPITAL')}</span>
											</Flex>
										}
									/>
								) : (
									<StatusComponent
										color="red"
										style={{ marginInlineStart: 20 }}
										bordered
										text={
											<Flex gap={5} align="center">
												<span>{t('ACTIONS.ISSUED_CAPITAL_IS')}</span>

												<span className="fw-medium mx-1 d-flex align-items-center gap-1" style={{ direction: 'ltr' }}>
													<Riyal size={13} />
													<NumberElement
														style={{ fontSize: 15, fontWeight: 500 }}
														value={+authorizedCapital?.issued_capital.replaceAll(',', '')!}
														decimalScale={2}
													/>
												</span>

												<span>{t('ACTIONS.IS_BEYOND_AUTHORIZED_CAPITAL')}</span>
											</Flex>
										}
									/>
								)}
							</Flex>

							<Flex justify="space-between" align="center" className="mt-5">
								<Checkbox onChange={(event) => setAcknowledge(event.target.checked)}>
									<span className="fw-semibold d-block">{t('ACTIONS.AGREEMENT_TO_TERMS_AUTHORIZED_CAPITALS')}</span>
								</Checkbox>
								<Button type="primary" className="px-4" disabled={!acknowledge || newRatio > 100} loading={isSubmitting} htmlType="submit">
									{t('COMMON.UPDATE')}
								</Button>
							</Flex>
						</Form>
					</Card>
				</>
			)}
		</>
	);
};

export default UpdateAuthorizedCapitalPage;
